/**
 * ComponentGallery — Gallery family renderer.
 *
 * Images displayed in a responsive grid, with content flowing below.
 *
 * Variants:
 *   - "gallery": uniform grid with gaps (all images same size)
 *   - "gallery_masonry": masonry-style (varying heights, CSS columns)
 *   - "gallery_full": seamless edge-to-edge photo-wall — fixed-aspect
 *     cells, no gaps, images fill each cell via overflow-hidden.
 *   - "gallery_carousel": auto-advancing pages of images with crossfade
 *   - "gallery_drift": Ken Burns slow zoom/pan on each grid tile
 *
 * Edit mode: add-image button appears after the gallery grid. Empty
 * buckets show placeholder affordances only on hover.
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import {
  bucketElements,
  BucketRow,
  RenderItem,
  BucketAddButton,
  EmptyBucketPlaceholder,
  elementHasRenderableMedia,
  coverFrameRounding,
  COVER_WRAPPER_UNROUND,
  COVER_WRAPPER_UNROUND_KEEP_IMG,
  SortableItemWrapper,
} from "./componentModeUtils";
import { buildDriftPresets } from "./driftUtils";
import { EditContext } from "../../../editor/context/EditContext";
import { DragHandleProvider } from "../../../editor/context/DragHandleContext";
import { makeSortableId } from "../../../editor/utils/reorderCache";

// `COVER_FILL` makes the media fill the tile and crop with `object-cover`. The
// `!items-center`/`!justify-center` overrides neutralise the per-image `position`
// flex alignment from `Image.js` so cover-cropped media stays centered instead of
// being parked in its `position` corner (e.g. bottom-right).
//
// `[&_.image-size-box]:!p-0` zeroes the image's OWN padding inside a cover tile.
// `Image.js` applies `container_style.rounded_border` to the <img> but its padding
// to the surrounding `.image-size-box` sizing wrapper. In a contained layout that
// padding correctly becomes space *around* the rounded image. In a cover tile,
// though, the tile IS the frame (its size/aspect are layout-owned, like the reset
// margins) — so honoring per-image padding insets the cover <img> inside an
// `overflow-hidden` square wrapper and the bitmap ends up cropped to straight
// corners. Resetting it here lets the cover <img> fill the tile edge-to-edge and
// keep its own rounded corners regardless of how much padding the image carries.
//
// `[&_img]:!box-border` switches the <img> to `box-sizing: border-box` so that
// any CSS border the image carries (from `base_style.border_enabled`) lives
// INSIDE the 100% tile area instead of overflowing it. Without this, the border
// ring (which has no border-radius in the drift tile, where `COVER_WRAPPER_UNROUND`
// strips `[&_img]:!rounded-none`) overflows the `overflow-hidden` frame and is
// completely clipped — invisible. With `border-box`, the border is within the
// frame boundary, and for drift tiles the frame's `clip-path: inset(0 round <r>)`
// rounds the border corners to match the configured radius.
const COVER_FILL = "[&_div]:!h-full [&_div]:!w-full [&_div]:!max-w-none [&_div]:!items-center [&_div]:!justify-center [&_.image-size-box]:!p-0 [&_.progressive-image-wrapper]:!h-full [&_.progressive-image-wrapper]:!w-full [&_.progressive-image-wrapper]:!max-w-none [&_img]:!w-full [&_img]:!h-full [&_img]:!object-cover [&_img]:!object-center [&_img]:!max-h-none [&_img]:!max-w-none [&_img]:!box-border [&_video]:!w-full [&_video]:!h-full [&_video]:!object-cover [&_video]:!object-center";
// The grid tile is a square cover box that imposes NO rounding of its own — the
// cover `<img>` fills it and keeps its OWN `container_style.rounded_border` (only
// the ProgressiveImage wrapper's hardcoded 8px is neutralized). So the tile's
// visible corner radius always equals the image's own, in preview AND the editor
// (where the renderer only has minimal refs). See ComponentMosaic /
// COVER_WRAPPER_UNROUND_KEEP_IMG for the full rationale.
const GALLERY_TILE = `overflow-hidden ${COVER_FILL} ${COVER_WRAPPER_UNROUND_KEEP_IMG}`;

/**
 * Width of a single cell in a centered flex-wrap grid that visually mimics a
 * `cols`-column CSS grid with `gapPx` gaps. Using flex-wrap + justify-center
 * (instead of `grid repeat(cols, 1fr)`) lets a partially-filled last row stay
 * centered instead of left-aligned, while each cell keeps the same width it
 * would have in the equivalent grid.
 */
const flexCellWidth = (cols, gapPx) =>
  `calc(${100 / cols}% - ${((cols - 1) * gapPx) / cols}px)`;

function isFullBleed(modeId) {
  return modeId === "gallery_full";
}

function isCarousel(modeId) {
  return modeId === "gallery_carousel";
}

function isDrift(modeId) {
  return modeId === "gallery_drift";
}

function getColumnCount(breakpoint, variant) {
  if (variant === "masonry") {
    if (breakpoint === "extra_small") return 1;
    if (breakpoint === "small") return 2;
    if (breakpoint === "medium") return 3;
    return breakpoint === "large" ? 3 : 4;
  }
  if (variant === "full") {
    if (breakpoint === "extra_small") return 2;
    if (breakpoint === "small") return 2;
    if (breakpoint === "medium") return 3;
    return 4;
  }
  if (breakpoint === "extra_small") return 1;
  if (breakpoint === "small") return 2;
  if (breakpoint === "medium") return 3;
  return 4;
}

// ---------------------------------------------------------------------------
// Gallery Carousel hook — pages of images auto-advance
// ---------------------------------------------------------------------------

// Slow, gentle pacing with a TRUE crossfade between pages — every page stays
// mounted (stacked) and only their opacity changes, so the outgoing page fades
// 1→0 while the incoming fades 0→1 at the same time (no fade-to-blank flash).
const CAROUSEL_INTERVAL_MS = 7000;
const CAROUSEL_FADE_MS = 1200;

function useGalleryCarousel(totalImages, perPage, { paused = false } = {}) {
  const pageCount = Math.max(Math.ceil(totalImages / Math.max(perPage, 1)), 1);
  const [activePage, setActivePage] = useState(0);
  const timerRef = useRef(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false;
  }, []);

  useEffect(() => {
    setActivePage(0);
  }, [totalImages, perPage]);

  useEffect(() => {
    if (paused || prefersReducedMotion || pageCount <= 1) return;

    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      setActivePage((prev) => (prev + 1) % pageCount);
    };

    const start = () => {
      if (timerRef.current) return;
      timerRef.current = setInterval(tick, CAROUSEL_INTERVAL_MS);
    };
    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [pageCount, paused, prefersReducedMotion]);

  return { activePage, pageCount };
}

// ---------------------------------------------------------------------------
// Drift (Ken Burns) presets
// ---------------------------------------------------------------------------

// Ken Burns motion intents (subtler than mosaic) authored as scale + translate
// fractions. Run through `buildDriftPresets`, which clamps the translate so the
// tile ALWAYS covers its frame. The previous presets zoomed all the way out to
// `scale(1)` while still translating, which (with zero overflow at scale 1)
// immediately exposed the frame edge/background (eng-00030). Minimum scale is
// now kept above 1 so the pan always has overflow to move within.
const DRIFT_INTENTS = [
  { fromScale: 1.08, fromTx: 0, fromTy: 0, toScale: 1.18, toTx: -0.03, toTy: -0.02 },
  { fromScale: 1.1, fromTx: 0.02, fromTy: 0, toScale: 1.2, toTx: -0.02, toTy: 0.03 },
  { fromScale: 1.08, fromTx: -0.02, fromTy: 0.02, toScale: 1.16, toTx: 0.03, toTy: -0.02 },
  { fromScale: 1.1, fromTx: 0, fromTy: -0.02, toScale: 1.2, toTx: -0.025, toTy: 0.02 },
  { fromScale: 1.09, fromTx: 0.015, fromTy: 0.015, toScale: 1.18, toTx: -0.02, toTy: -0.03 },
  { fromScale: 1.08, fromTx: -0.02, fromTy: -0.01, toScale: 1.19, toTx: 0.03, toTy: 0.02 },
];

const DRIFT_PRESETS = buildDriftPresets(DRIFT_INTENTS);

const DRIFT_DURATION_MS = 12000;

// ---------------------------------------------------------------------------
// Renderer
// ---------------------------------------------------------------------------

const ComponentGallery = ({ component, elements, modeDefinition, breakpoint }) => {
  const modeId = component?.component_mode;
  const isMasonry = modeId === "gallery_masonry";
  const fullBleed = isFullBleed(modeId);
  const carousel = isCarousel(modeId);
  const drift = isDrift(modeId);
  const { bucketed } = bucketElements(elements, modeDefinition);
  const editContext = useContext(EditContext);
  const isEditMode = !!editContext;

  const images = bucketed.image || [];
  const titles = bucketed.title || [];
  const texts = bucketed.text || [];
  const buttonCollections = bucketed.button_collection || [];
  const buttons = bucketed.button || [];
  const files = bucketed.file || [];

  const compId = component?.id;
  const variant = isMasonry ? "masonry" : fullBleed ? "full" : "default";
  const cols = getColumnCount(breakpoint, variant);
  const totalElements = elements?.length || 0;

  const perPage = cols * 2;
  const { activePage, pageCount } = useGalleryCarousel(
    images.length, perPage, { paused: !carousel }
  );

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false;
  }, []);

  const imagesHaveMedia = images.length > 0 && images.some(elementHasRenderableMedia);
  // The carousel + drift layouts force EVERY image into a fixed-size,
  // overflow-hidden (and, for drift, animated) cover frame. A "No Image
  // Selected" placeholder dragged into that frame is squished/clipped. Only use
  // those cover layouts when every image has media; otherwise fall back to the
  // contained grid (which renders placeholders naturally). The full-bleed grid
  // already handles placeholders per-cell, so it keeps using `imagesHaveMedia`.
  const imagesAllHaveMedia =
    images.length > 0 && images.every(elementHasRenderableMedia);

  // ---- Per-image reordering (drag to move within the component) ----------
  // Mirror BucketRow's per-item sortable wiring for the layouts that render
  // media through bare RenderItem (drift, full-bleed). Without this each image's
  // EditableElement inherits the component's drag context, so the drag "dots"
  // move the whole component and render as a pill clipped inside the
  // overflow-hidden cover tile. Making the cover tile itself the sortable node
  // (drag-anywhere → no handle pill) lets dragging reorder the image and lets
  // the selection chrome portal above the tile. See ComponentMosaic for the
  // matching treatment.
  const imageSortData = (img, index) => ({
    parentType: "component",
    parentId: compId,
    kind: "item",
    itemType: img.type,
    index,
    autoLayout: true,
    autoBucket: null,
  });

  const renderMediaTile = (img, index, className, style, inner) => {
    const media = inner ?? <RenderItem element={img} />;
    if (!isEditMode) {
      return (
        <div key={img.id} className={className} style={style}>
          {media}
        </div>
      );
    }
    // The tile is the per-image drag-to-reorder sortable node. Images
    // intentionally do NOT get an inter-item "+" add button — it clutters the
    // gallery; new images are added via the trailing bucket "add" button. The
    // tile is `relative` (or already a positioning context) so the selection
    // chrome anchors to it.
    const anchorClass = /\b(absolute|fixed|relative)\b/.test(className) ? "" : "relative";
    return (
      <SortableItemWrapper
        key={img.id}
        id={makeSortableId(img)}
        data={imageSortData(img, index)}
        className={`${anchorClass} ${className}`.trim()}
        style={style}
      >
        {media}
      </SortableItemWrapper>
    );
  };

  const imageSortIds = images.map((img) => makeSortableId(img));
  const withImageSortContext = (node) => {
    if (!isEditMode || imageSortIds.length === 0) return node;
    return (
      <SortableContext items={imageSortIds} strategy={rectSortingStrategy}>
        {node}
      </SortableContext>
    );
  };

  // For non-reorderable image slots (empty placeholders, and the auto-advancing
  // carousel tiles), neutralize the inherited component drag context so the
  // broken/clipped drag pill never appears. The image stays selectable.
  const SuppressDrag = ({ children }) =>
    isEditMode ? (
      <DragHandleProvider value={{ dragListeners: null, dragAnywhereEnabled: false }}>
        {children}
      </DragHandleProvider>
    ) : (
      children
    );

  const renderCarouselGallery = () => {
    if (images.length === 0) return null;

    if (!imagesAllHaveMedia) {
      return (
        <BucketRow
          elements={images}
          compId={compId}
          className="w-full flex flex-wrap justify-center"
          style={{ gap: "8px" }}
        />
      );
    }

    // Stack every page absolutely and crossfade by opacity. All pages stay
    // mounted so the outgoing page fades 1→0 while the incoming fades 0→1 at
    // the same time (true crossfade, no fade-to-blank). The first page is also
    // rendered invisibly in normal flow to size the (relative) container.
    const pages = Array.from({ length: pageCount }, (_, p) =>
      images.slice(p * perPage, p * perPage + perPage),
    );

    const renderPageGrid = (pageImages) => (
      <div className="w-full flex flex-wrap justify-center" style={{ gap: "4px" }}>
        {pageImages.map((img) => (
          <div key={img.id} className={`relative overflow-hidden rounded-lg flex-shrink-0 ${COVER_FILL}`} style={{ width: flexCellWidth(cols, 4), aspectRatio: "4 / 3" }}>
            <div className="absolute inset-0">
              <SuppressDrag>
                <RenderItem element={img} />
              </SuppressDrag>
            </div>
          </div>
        ))}
      </div>
    );

    return (
      <div className="w-full relative" style={{ minHeight: "200px" }}>
        <div className="invisible" aria-hidden="true">
          {renderPageGrid(pages[0] || [])}
        </div>
        {pages.map((pageImages, p) => (
          <div
            key={p}
            className="absolute inset-0 transition-opacity ease-in-out"
            style={{
              transitionDuration: `${CAROUSEL_FADE_MS}ms`,
              opacity: p === activePage ? 1 : 0,
              pointerEvents: p === activePage ? "auto" : "none",
            }}
          >
            {renderPageGrid(pageImages)}
          </div>
        ))}
        {pageCount > 1 && (
          <div className="flex justify-center gap-1.5 mt-2">
            {Array.from({ length: pageCount }, (_, i) => (
              <span
                key={i}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === activePage
                    ? "bg-gray-800 dark:bg-white scale-110"
                    : "bg-gray-300 dark:bg-gray-600"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDriftGallery = () => {
    if (images.length === 0) return null;

    if (!imagesAllHaveMedia) {
      return (
        <BucketRow
          elements={images}
          compId={compId}
          className="w-full flex flex-wrap justify-center"
          style={{ gap: "8px" }}
        />
      );
    }

    return withImageSortContext(
      <div
        className="w-full flex flex-wrap justify-center"
        style={{ gap: "4px" }}
      >
        {images.map((img, idx) => {
          const durationSec = (DRIFT_DURATION_MS + idx * 1500) / 1000;
          const delaySec = (idx * 1800) / 1000;
          // The frame's rounding is the image's own configured radius
          // (container_style.rounded_border), not a hardcoded value.
          const { className: roundedClass, clip } = coverFrameRounding(img);

          // The tile is the static rounded "frame" AND the sortable node. Its
          // `clip-path` rounded inset clips the zooming (transformed/composited)
          // child to exactly the image's configured rounded border, so the
          // frame's corners stay rounded while the Ken Burns zoom scales inside.
          // The drag transform (during reorder) lands on the frame while the Ken
          // Burns animation stays on the inner layer — the two never conflict.
          const frameClass = `relative overflow-hidden ${roundedClass} flex-shrink-0 ${COVER_FILL} ${COVER_WRAPPER_UNROUND}`;
          const frameStyle = { width: flexCellWidth(cols, 4), aspectRatio: "1 / 1", clipPath: clip, WebkitClipPath: clip };
          const inner = (
            <div
              className="absolute inset-0"
              style={prefersReducedMotion ? {} : {
                animation: `gallery-drift-${idx % DRIFT_PRESETS.length} ${durationSec}s ease-in-out ${delaySec}s infinite alternate`,
              }}
            >
              <RenderItem element={img} />
            </div>
          );

          return renderMediaTile(img, idx, frameClass, frameStyle, inner);
        })}
        {!prefersReducedMotion && (
          <style>{DRIFT_PRESETS.map((preset, i) => `
            @keyframes gallery-drift-${i} {
              from { transform: ${preset.from}; }
              to { transform: ${preset.to}; }
            }
          `).join("")}</style>
        )}
      </div>,
    );
  };

  const contentItems = [...titles, ...texts, ...buttonCollections, ...buttons]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const GalleryGrid = images.length > 0 ? (
    carousel ? (
      renderCarouselGallery()
    ) : drift ? (
      renderDriftGallery()
    ) : isMasonry ? (
      <BucketRow
        elements={images}
        compId={compId}
        className="w-full"
        style={{ columnCount: cols, columnGap: "8px" }}
        cellClassName="overflow-hidden rounded-lg mb-2"
        cellStyle={{ breakInside: "avoid" }}
      />
    ) : fullBleed ? (
      withImageSortContext(
        <div className="w-full flex flex-wrap justify-center" style={{ gap: 0 }}>
          {(() => {
            // Edge-to-edge photo-wall: when there are fewer images than the
            // breakpoint column count they must still span the FULL component
            // width (otherwise a single short row sits centered with empty
            // margins). Capping the columns at the image count makes a non-full
            // row stretch its cells to fill 100% of the width; full multi-row
            // galleries keep the breakpoint column count.
            const fullBleedCols = Math.min(cols, images.length);
            return images.map((img, idx) => {
            const hasMedia = elementHasRenderableMedia(img);
            const cellWidth = flexCellWidth(fullBleedCols, 0);
            // Placeholders ("No Image Selected") must not be forced into the
            // square, overflow-hidden cover cell — their min dimensions exceed
            // the cell and get clipped. Render them naturally and centered so the
            // full dashed-border affordance stays visible, while real media keeps
            // the edge-to-edge full-bleed cover treatment.
            if (!hasMedia) {
              return (
                <div
                  key={img.id}
                  className="relative flex items-center justify-center p-2"
                  style={{ width: cellWidth }}
                >
                  <SuppressDrag>
                    <RenderItem element={img} />
                  </SuppressDrag>
                </div>
              );
            }
            // Each image fills its full cell (square + object-cover). Cells are
            // sized off `fullBleedCols`, so a row with fewer images than the
            // breakpoint columns stretches edge-to-edge across the component.
            return renderMediaTile(
              img,
              idx,
              "relative overflow-hidden flex-shrink-0",
              { width: cellWidth, aspectRatio: "1 / 1" },
              <div className={`absolute inset-0 ${COVER_FILL}`}>
                <RenderItem element={img} />
              </div>,
            );
            });
          })()}
        </div>,
      )
    ) : (
      <BucketRow
        elements={images}
        compId={compId}
        className="w-full flex flex-wrap justify-center"
        style={{ gap: "8px" }}
        cellClassName={GALLERY_TILE}
        cellStyle={{ width: flexCellWidth(cols, 8), aspectRatio: "4 / 3" }}
      />
    )
  ) : null;

  return (
    <div className="w-full flex flex-col group/gallery">
      {GalleryGrid ? (
        <>
          {GalleryGrid}
          {isEditMode && (
            <BucketAddButton compId={compId} bucket="image" order={totalElements} className="-mt-4 z-10" />
          )}
        </>
      ) : isEditMode ? (
        <EmptyBucketPlaceholder compId={compId} bucket="image" order={totalElements} />
      ) : null}

      {contentItems.length > 0 && (
        <BucketRow elements={contentItems} compId={compId} className="w-full" />
      )}

      {isEditMode && (
        <>
          {titles.length === 0 && (
            <EmptyBucketPlaceholder compId={compId} bucket="title" order={totalElements} />
          )}
          {texts.length === 0 && (
            <EmptyBucketPlaceholder compId={compId} bucket="text" order={totalElements} />
          )}
          {contentItems.length > 0 && (
            <BucketAddButton compId={compId} bucket="text" order={totalElements} />
          )}
        </>
      )}

      {files.length > 0 && (
        <BucketRow elements={files} compId={compId} className="w-full" />
      )}
    </div>
  );
};

export default React.memo(ComponentGallery);
